
import { getCurrencyRate } from "@/api/catalogue";
import { searchWebmasters } from "@/api/org-cabinets-backend";
import { getAffiliateInvoices, getEmployeeMethods } from "@/api/payout";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import BaseDateTimePicker from "@/components-UI/BaseDateTimePicker.vue";
import BaseAutocomplete from "@/components/base/design/BaseAutocomplete.vue";
import paymentCurrencies from "@/mappings/paymentCurrencies";
import { showServerError } from "@/utils";
import { getCurrentDate } from "@/utils/formatDates";
import { formatPhoneInput, translatePaymentsRequisite } from "@/utils/translate";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Component, PropSync, Ref, Vue, Watch } from "vue-property-decorator";
import { ICreatePayment, IEmployeeMethod, IMyLeadgidInvoice, IPaymentRequisite } from "../../../../api/types/payout";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import BaseCard from "../../../base/BaseCard.vue";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import EditModeTextField from "../../../base/form/EditModeTextField.vue";

const BLOCKED_METHODS = [
    "custom_v1",
    "transfer_v1",
    "bank_card_europe_v1",
    "yandex_v1",
    "contract_eur_individual_v1",
    "contract_eur_legal_v1",
    "contract_eur",
    "payoneer_v1",
    "paypal_v1",
    "debt_v1",
    "netting_v1",
] as const;

const VALUES_WITH_COMMISSION: Record<string, number> = {
    tether_v1: 0,
    bank_card_ru_v1: 8.5,
    bank_card_ua_v1: 8.5,
    capitalist_v1: 5,
    capitalist_kz_v1: 5,
};

const REQUISITE_HINTS: Record<string, string> = {
    yoomoney_v1: "15-16 символов без пробелов.\nПример: 4400167891234567",
    webmoney_usd_v1: "13 символов без пробелов.\nПример: Z123456789123",
    webmoney_v1: "13 символов без пробелов.\nПример: Z123456789123",
    qiwi_rub_v1: "Номер телефона без +, только цифры.\nПример: 79501234567",
    qiwi_v1: "Номер телефона без +, только цифры.\nПример: 79501234567",
    bank_card_ru_ua_v1: "13-19 символов без пробелов.\nПример: 1234123412341234",
};

// Для bank_card_ru_v1 набор реквизитов разный — подсказка своя под каждое поле.
const BANK_CARD_RU_HINTS: Record<string, string> = {
    card_number: "13-19 цифр без пробелов.\nПример: 5368290053996361",
    phone: "+7 и 10 цифр.\nПример: +79001234567",
};

const QIWI_PHONE_LENGTH = 11;
const INVOICE_LIMIT = 500;
const AVAILABLE_DATES = { start: null, end: null, days: [1, 15] } as const;

interface CurrencyItem {
    itemText: string;
    itemValue: string;
}

const DEFAULT_REQUISITE: IPaymentRequisite = {
    partner_data: {},
    manager_data: {},
};

@Component({
    components: {
        BaseAutocomplete,
        EditModeTextField,
        BaseCard,
        BaseDateTimePicker,
    },
})

export default class CreatePaymentsBody extends Vue {
    @PropSync("payment", { required: true }) paymentSync!: ICreatePayment;
    @PropSync("isBodyValid", { required: true }) isBodyValidSync!: boolean;

    @Ref("currencyInput") currencyInput!: BaseAutocomplete;

    translatePaymentsRequisite = translatePaymentsRequisite;
    searchWebmasters = searchWebmasters;
    currencies = paymentCurrencies;
    methods: IEmployeeMethod[] = [];
    unpaidInvoices: IMyLeadgidInvoice[] = [];
    unpaidInvoice: Partial<IMyLeadgidInvoice> = {};
    isManagerFieldsRequired: boolean = false;
    isContract: boolean = false;
    requisites: string[] = [];
    requisitesForCreate: IPaymentRequisite = { ...DEFAULT_REQUISITE };
    currentAffiliateId: number | null = null;
    paidSumValue: number | null = null;
    commission: number | null = null;
    rateValue: number | null = null;
    rateOperation: "division" | "multiplication" = "multiplication";
    availableDates = AVAILABLE_DATES;
    isDisableRate: boolean = false;

    private currencyItemsCache: CurrencyItem[] | null = null;

    get getCurrencyItems(): CurrencyItem[] {
        if (!this.currencyItemsCache) {
            this.currencyItemsCache = this.currencies.map((currency) => ({
                itemText: currency,
                itemValue: currency,
            }));
        }
        return this.currencyItemsCache;
    }

    get getCurrencyItemsForPrepayment(): CurrencyItem[] {
        return this.getCurrencyItems.filter((item) => item.itemValue !== "USDT");
    }

    get getAvailablePaidSum(): string {
        const { total_amount, partials_amount, currency } = this.unpaidInvoice;
        if ((!total_amount && total_amount !== 0) || (!partials_amount && partials_amount !== 0) || !currency) {
            return "";
        }
        const availableSum = total_amount - partials_amount;
        return `${availableSum.toLocaleString("ru-RU", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        })} ${currency}`;
    }

    get isInvoice(): boolean {
        return this.paymentSync.payment_type === "invoice_payment";
    }

    get isTether(): boolean {
        return this.paymentSync.payment_method === "tether_v1";
    }

    get isWithCommission(): boolean {
        const method = this.paymentSync.payment_method;
        return method ? method in VALUES_WITH_COMMISSION : false;
    }

    // Единый источник правды: то же значение, что уходит на бэк (см. addRequisite).
    cleanRequisite(item: string, value: string): string {
        if (item === "card_number") return String(value ?? "").replace(/\D/g, "");
        if (item === "phone") return formatPhoneInput(value).replace(/[^\d+]/g, "");
        return value;
    }

    isRequisiteFormatValid(item: string, v: string): boolean {
        if (this.paymentSync.payment_method !== "bank_card_ru_v1") return true;
        const cleaned = this.cleanRequisite(item, v);
        if (item === "card_number") return /^\d{13,19}$/.test(cleaned);
        if (item === "phone") return /^\+7\d{10}$/.test(cleaned);
        if (item === "cardholder_name" || item === "bank_name") return (cleaned?.length ?? 0) >= 4;
        return true;
    }

    validationRules(item: string): Array<(v: string) => boolean | string> {
        return [
            (v: string) => !!v || "Поле не должно быть пустым",
            (v: string) => {
                if (this.paymentSync.payment_method === "qiwi_v1") {
                    return v?.length === QIWI_PHONE_LENGTH || "Длина номера должна быть 11 символов";
                }
                return true;
            },
            (v: string) => {
                if (item === "card_number") {
                    return this.isRequisiteFormatValid(item, v) || "Номер карты должен содержать 13-19 цифр без пробелов";
                }
                return true;
            },
            (v: string) => {
                if (item === "phone") {
                    return this.isRequisiteFormatValid(item, v) || "Телефон должен быть в формате +7XXXXXXXXXX";
                }
                return true;
            },
            (v: string) => {
                if (item === "cardholder_name" || item === "bank_name") {
                    return this.isRequisiteFormatValid(item, v) || "Поле должно содержать не менее 4 символов";
                }
                return true;
            },
        ];
    }

    get getRequisiteFieldsType(): string {
        return this.isManagerFieldsRequired ? "manager_data" : "partner_data";
    }

    @Watch("paymentSync.payment_method")
    @Watch("isWithCommission")
    triggerUpdateCommission(): void {
        const method = this.paymentSync.payment_method;
        if (!method) return;

        const value = this.isTether ? null : VALUES_WITH_COMMISSION[method] ?? 0;
        this.updateCommission(value);
        this.commission = value;
        this.validationHandler();
    }

    @Watch("paymentSync.payment_type")
    clearLocalRequisites(): void {
        this.requisites = [];
    }

    get calculateCommission(): string {
        if (!this.paidSumValue || !this.commission) {
            return "0.00";
        }
        return (this.paidSumValue * (this.commission / 100)).toFixed(2);
    }

    updateCommission(value: number | '' | null): void {
        value = value === '' ? null : value;
        const val = value ? parseFloat((value / 100).toFixed(2)) : value === 0 ? 0 : null;
        this.input("commission", val);
        this.validationHandler();
    }

    get calculateTotalPayout(): number {
        if (!this.paidSumValue) {
            return 0;
        }

        const commissionAmount = this.commission ? this.paidSumValue * (this.commission / 100) : 0;
        const withoutCommission = this.paidSumValue - commissionAmount;

        if (!this.rateValue || this.rateValue <= 0 || this.isDisableRate) {
            return parseFloat(withoutCommission.toFixed(2));
        }

        const result = this.rateOperation === "multiplication"
            ? withoutCommission * this.rateValue
            : withoutCommission / this.rateValue;

        return parseFloat(result.toFixed(2));
    }

    @Watch("calculateTotalPayout")
    updateFinalPayout(): void {
        this.input("final_payout", this.calculateTotalPayout);
    }

    get totalPayoutCurrency(): string {
        return this.paymentSync.exchange_rates?.currency ?? "";
    }

    async created(): Promise<void> {
        await this.getEmployeeMethods();
        this.currentAffiliateId = this.paymentSync.affiliate_id!;
    }

    async getEmployeeMethods(): Promise<void> {
        try {
            const { data } = await getEmployeeMethods({ is_active: 1 });
            this.methods = data
                .filter((item) => !BLOCKED_METHODS.includes(item.id as any))
                .map((method) => ({ ...method, itemText: method.name.ru }));
        } catch (err) {
            showServerError(err, "Список способов выплат не загружен");
        }
    }

    async getUnpaidInvoice(): Promise<void> {
        try {
            const { data } = await getAffiliateInvoices({
                offset: 0,
                limit: INVOICE_LIMIT,
                affiliate_id: [this.paymentSync.affiliate_id!],
                paid_status: ["partial_paid", "not_paid"],
            });
            this.unpaidInvoices = data.map((invoice) => ({
                ...invoice,
                itemText: `[${invoice.id}] - ${Number(invoice.total_amount).toFixed(2)} ${invoice.currency}`,
            }));
        } catch (err) {
            showServerError(err, "Список неоплаченных счетов не загружен");
        }
    }

    setInvoice(value: IMyLeadgidInvoice): void {
        this.unpaidInvoice = value;
        const sum = value.total_amount - value.partials_amount;
        this.input("invoice_id", value.id);
        this.input("available_sum", sum);

        this.input("currency", value.currency);
        this.input("commission", null);
        this.inputExchangeRates("rate", null);
        this.paidSumValue = null;
        this.commission = null;
        this.rateValue = null;
        this.updateIsDisableRate();
        this.updateRate();
    }

    input(prop: string, value: any): void {
        this.$set(this.paymentSync, prop, value);
    }

    updatePeriod(value: any): void {
        this.input("period", value);
        this.validationHandler();
    }

    updateCurrency(value: any): void {
        this.input("currency", value);
        this.updateIsDisableRate();
        this.updateRate();
    }

    inputExchangeRates(prop: "currency" | "rate", value: string | number | null): void {
        if (!this.paymentSync.exchange_rates) {
            return;
        }
        this.$set(this.paymentSync.exchange_rates, prop, value);

        if (prop === "currency") {
            this.rateValue = null;
            this.updateIsDisableRate();
            this.updateRate();
        }

        this.validationHandler();
    }

    updateIsDisableRate(): void {
        const invoiceCurrency = this.paymentSync.currency;
        const payoutCurrency = this.paymentSync.exchange_rates?.currency;
        this.isDisableRate = !invoiceCurrency || !payoutCurrency || invoiceCurrency === payoutCurrency;
        if (this.isDisableRate) {
            this.rateValue = null;
        }
    }

    validationHandler(): void {
        const { currency: invoiceCurrency, exchange_rates: { currency: payoutCurrency }, commission, payment_method, paid_sum, period, requisite } = this.paymentSync;

        // ставка
        const isWithRate = invoiceCurrency && payoutCurrency && invoiceCurrency !== payoutCurrency;
        const isRateValid = isWithRate ? !!this.rateValue && this.rateValue > 0 : true;
        const isExchangesRatesFully = isRateValid && !!payoutCurrency;
        // комиссия
        const isCommissionValid = this.isWithCommission ? commission !== null : true;
        // реквизиты
        let isRequisiteFully = false;
        const isCharity = payment_method === "Charity_v1";
        const isTicket = payment_method === "tickets_v1";
        const isQiwi = payment_method === "qiwi_v1";
        const requisiteFields = this.requisitesForCreate[this.getRequisiteFieldsType];

        if (isCharity || isTicket) {
            isRequisiteFully = true;
        } else if (isQiwi) {
            const accountId = requisiteFields?.account_id;
            isRequisiteFully = typeof accountId === "string" && accountId.length === QIWI_PHONE_LENGTH;
        } else if (payment_method === "bank_card_ru_v1") {
            isRequisiteFully = this.requisites.every(
                (item) => this.isRequisiteFormatValid(item, requisiteFields?.[item] ?? ""),
            );
        } else {
            isRequisiteFully = !!requisiteFields[this.requisites[0]];
        }

        // общая валидация
        const isRequiredFieldsValid = !!paid_sum && !!period && !!payment_method && !!requisite && !!invoiceCurrency;

        this.isBodyValidSync = isRequiredFieldsValid && isRequisiteFully && isCommissionValid && isExchangesRatesFully;
    }

    async updateRate(): Promise<void> {
        if (this.isDisableRate || !this.paymentSync.exchange_rates) {
            return;
        }

        const currencyFrom = this.paymentSync.currency;
        const currencyTo = this.paymentSync.exchange_rates.currency;

        if (!currencyFrom || !currencyTo) {
            return;
        }

        try {
            const { original_rate, operation } = await getCurrencyRate({
                source: "excel",
                currency_from: currencyFrom,
                currency_to: currencyTo,
                date: getCurrentDate("yyyy-MM-dd"),
            });
            this.$set(this.paymentSync.exchange_rates, "rate", original_rate);
            this.rateValue = original_rate;
            this.rateOperation = operation;
            this.validationHandler();
        } catch (err: any) {
            if (err.status === 404) {
                this.$set(this.paymentSync.exchange_rates, "rate", 0);
                this.rateValue = 0;
                this.rateOperation = "multiplication";
            } else {
                showServerError(err, "Список валют не загружен");
            }
        }
    }

    changeMethod(methodId: string): void {
        this.$set(this.paymentSync, "payment_method", methodId);
        this.clearRequisites();

        const method = this.methods.find((m) => m.id === methodId);
        this.isContract = methodId === "contract_v1" || methodId === 'contract_kz';
        this.isManagerFieldsRequired = method?.manager_fields_required ?? false;

        if (this.isTether) {
            this.setCurrencyInput("USDT");
            this.inputExchangeRates("currency", "USDT");
        }

        const isCapitalist = methodId === "capitalist_v1" || methodId === "capitalist_kz_v1";
        if (isCapitalist) {
            this.setCurrencyInput("USD");
        }

        if (methodId === "Charity_v1") {
            this.requisites = [];
        } else {
            this.requisites = method?.manager_fields_required
                ? method?.manager_fields_keys ?? []
                : method?.partner_fields_keys ?? [];
        }
        this.validationHandler();
    }

    private setCurrencyInput(currency: string): void {
        if (this.currencyInput) {
            this.currencyInput.model = { name: currency, value: currency };
            this.currencyInput.changeItem();
        }
    }

    resetForm(): void {
        this.paymentSync.commission = undefined;
        this.paymentSync.currency = undefined;
        this.paymentSync.comment = "";
        this.paidSumValue = null;
        this.commission = null;
        this.clearRequisites();
    }

    clearRequisites(): void {
        this.paymentSync.requisite = this.requisitesForCreate = { ...DEFAULT_REQUISITE };
    }

    getRequisiteName(item: string): string | undefined {
        const requisite = this.paymentSync.requisite;
        if (!requisite) {
            return undefined;
        }
        return this.isManagerFieldsRequired
            ? requisite.manager_data?.[item]
            : requisite.partner_data?.[item];
    }

    addRequisite(item: string, value: string | { guid: string; name: string }): void {
        const fieldsType = this.getRequisiteFieldsType;

        if (item === "contract" && typeof value === "object" && value.guid) {
            this.requisitesForCreate[fieldsType] = {
                ...this.requisitesForCreate[fieldsType],
                legal_id: value.guid,
                legal_name: value.name,
            };
        } else if (typeof value === "string") {
            this.requisitesForCreate[fieldsType] = {
                ...this.requisitesForCreate[fieldsType],
                [item]: this.cleanRequisite(item, value),
            };
        }

        this.$set(this.paymentSync, "requisite", this.requisitesForCreate);
        this.validationHandler();
    }

    onDayClick(): void {
        if (this.$refs.datepicker) {
            // @ts-ignore
            this.$refs.datepicker.submitDate();
        }
    }

    @Watch("paymentSync.affiliate_id")
    async changeNearestPayouts(): Promise<void> {
        if (this.paymentSync.affiliate_id !== this.currentAffiliateId) {
            await this.getUnpaidInvoice();
            this.currentAffiliateId = this.paymentSync.affiliate_id!;
        }
    }

    @Watch("isInvoice", { immediate: true })
    async updateInvoices(value: boolean): Promise<void> {
        if (value) {
            await this.getUnpaidInvoice();
        }
        this.resetForm();
    }

    @Watch("paidSumValue")
    updatePaidSum(): void {
        this.input("paid_sum", this.paidSumValue);
        this.validationHandler();
    }

    hintRequisite(item: string): string {
        const method = this.paymentSync.payment_method;
        if (!method) return "";
        if (method === "bank_card_ru_v1") return BANK_CARD_RU_HINTS[item] ?? "";
        return REQUISITE_HINTS[method] ?? "";
    }
}
